import type { Dictionary } from '@ngrx/entity';
import { createSelector, type MemoizedSelector } from '@ngrx/store';
import type { Attribute, AttributeId } from '../../../interfaces/Attribute.interface';
import type { CategoryId } from '../../../interfaces/Category.interface';
import type { AppState } from '../../app.state';
import { selectEntitiesState } from '../entities-state.selector';
import { attributeAdapter, MapKey, type State } from './attributes.state';

export const selectAttributesState: MemoizedSelector<AppState, State> = createSelector(
    selectEntitiesState,
    entitiesState => entitiesState.attributes
);

export const selectAttributes: (state: AppState) => Attribute[] =
    attributeAdapter.getSelectors(selectAttributesState).selectAll;

export const selectAttributesMap: (state: AppState) => Dictionary<Attribute> =
    attributeAdapter.getSelectors(selectAttributesState).selectEntities;

const getIsPending = (state: State): boolean => state.isPending;
const getIsLoaded = (state: State): boolean => state.isLoaded;

export const selectIsPending: MemoizedSelector<AppState, boolean> = createSelector(selectAttributesState, getIsPending);
export const selectIsLoaded: MemoizedSelector<AppState, boolean> = createSelector(selectAttributesState, getIsLoaded);

export const selectAttributeById: (attributeId: AttributeId) => MemoizedSelector<AppState, Attribute | null> = (
    attributeId: AttributeId
) => createSelector(selectAttributesMap, attributesMap => attributesMap[attributeId] ?? null);

export const selectAttributeByAdvizeo: (advizeoCode: string) => MemoizedSelector<AppState, Attribute | null> = (
    advizeoCode: string
) =>
    createSelector(
        selectAttributes,
        attributes => attributes.find(attribute => attribute.advizeoCode === advizeoCode) ?? null
    );

export const selectAttributesByCategoryId: (categoryId: CategoryId) => MemoizedSelector<AppState, Attribute[]> = (
    categoryId: CategoryId
) => attributeAdapter.getSelectors(selectAttributesState).selectEntitiesByMappedId(MapKey.BY_CATEGORY_ID, categoryId);

export const selectAttributesByCategoryIds: (categoryIds: CategoryId[]) => MemoizedSelector<AppState, Attribute[]> = (
    categoryIds: CategoryId[]
) => attributeAdapter.getSelectors(selectAttributesState).selectEntitiesByMappedIds(MapKey.BY_CATEGORY_ID, categoryIds);

export const selectAttributeIdsByCategoryIds: (
    categoryIds: CategoryId[]
) => MemoizedSelector<AppState, AttributeId[]> = (categoryIds: CategoryId[]) =>
    createSelector(selectAttributesByCategoryIds(categoryIds), attributes =>
        attributes.map(attribute => attribute.attributeId)
    );

export const selectVisibleAttributesByCategoryId: (
    categoryId: CategoryId
) => MemoizedSelector<AppState, Attribute[]> = (categoryId: CategoryId) =>
    createSelector(selectAttributesByCategoryId(categoryId), attributes =>
        attributes.filter(attribute => attribute.isVisible)
    );

export const selectVisibleAttributesByCategoryIds: (
    categoryIds: CategoryId[]
) => MemoizedSelector<AppState, Attribute[]> = (categoryIds: CategoryId[]) =>
    createSelector(selectAttributesByCategoryIds(categoryIds), attributes =>
        attributes.filter(attribute => attribute.isVisible)
    );
